/*
 * Renders a group of checkboxes that can be used to select
 * multiple values from a set of options.
 *
 * The corresponding component when rendering the selected
 * values is PropertyGroup.
 *
 */

import React from 'react';
import { arrayOf, bool, node, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import { FieldCheckbox, SecondaryButton, ValidationError } from '../../components';

import css from './FieldCheckboxGroup.module.css';

const subtextMap = {
  "EditListingDetailsForm.pub_proofOfLife": "Parnexus reviews your proof before publishing. Your proof is shared directly to interested parties at your own discretion.",
  "EditListingDetailsForm.pub_buyerProof": "Parnexus reviews your proof before publishing. Your proof is shared directly to interested parties at your own discretion.",
  "EditListingDetailsForm.pub_dealSizeBracketFilter": "Ensure accuracy. This informs our listing filters, and who is notified of your deal.",
  "EditListingDetailsForm.pub_ageYachtsFilter": "This informs our listing filters, and who is notified of your deal.",
  "EditListingDetailsForm.pub_ageCarsFilter": "This informs our listing filters, and who is notified of your deal.",
  "EditListingDetailsForm.pub_ageJetsFilter": "This informs our listing filters, and who is notified of your deal.",
  "EditListingDetailsForm.pub_ageWineFilter": "This informs our listing filters, and who is notified of your deal.",
};

const FieldCheckboxRenderer = props => {
  const {
    className,
    rootClassName,
    label,
    optionLabelClassName,
    twoColumns,
    id,
    fields,
    options,
    meta,
    onChange,
    useAllButton,
    onSelectAll,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const listClasses = twoColumns ? classNames(css.list, css.twoColumns) : css.list;

  const subtext = subtextMap[id];

  return (
    <fieldset className={classes}>
      {label ? <legend>{label}</legend> : null}
      {subtext ? <div className={css.subtext}>
        {subtext}
      </div> : null}
      {useAllButton ? <SecondaryButton
        type="button"
        className={css.allButton}
        onClick={() => onSelectAll()}
      >
        Select All
      </SecondaryButton> : null}

      <ul className={listClasses}>
        {options.map((option, index) => {
          const fieldId = `${id}.${option.key}`;
          const textClassName = optionLabelClassName;
          const textClassNameMaybe = textClassName ? { textClassName } : {};
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox
                id={fieldId}
                name={fields.name}
                label={option.label}
                value={option.key}
                onChange={onChange}
                disabled={!!option.disabled}
                {...textClassNameMaybe}
              />
            </li>
          );
        })}
      </ul>
      <ValidationError fieldMeta={{ ...meta }} />
    </fieldset>
  );
};

FieldCheckboxRenderer.defaultProps = {
  rootClassName: null,
  className: null,
  label: null,
  twoColumns: false,
  useAllButton: false,
  onSelectAll: () => {},
};

FieldCheckboxRenderer.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  options: arrayOf(
    shape({
      key: string.isRequired,
      label: node.isRequired,
    })
  ).isRequired,
  twoColumns: bool,
};

const FieldCheckboxGroup = props => <FieldArray component={FieldCheckboxRenderer} {...props} />;

// Name and component are required fields for FieldArray.
// Component-prop we define in this file, name needs to be passed in
FieldCheckboxGroup.propTypes = {
  name: string.isRequired,
};

export default FieldCheckboxGroup;
